import { useState } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { EnergyFlow } from "../lib/types";

interface Props {
  flow: EnergyFlow;
}

const DAY_LABELS: Record<string, string> = {
  summer_average: "Gemiddelde zomerdag",
  winter_average: "Gemiddelde winterdag",
  negative_price_day: "Dag met negatieve uren",
  evening_peak_day: "Dag met avondpiek",
};

export function DayInLifeChart({ flow }: Props) {
  const days = Object.keys(flow.day_in_life);
  const [selected, setSelected] = useState<string>(days[0] ?? "summer_average");
  const day = flow.day_in_life[selected];

  if (!day) return null;

  const data = day.hours.map((h, i) => ({
    uur: `${String(h).padStart(2, "0")}:00`,
    Solar: round1(day.production_kw[i]),
    Verbruik: round1(day.consumption_kw[i]),
    Accu: round1(day.battery_soc_kwh[i]),
    Import: round1(day.import_kw[i]),
    Export: round1(day.export_kw[i]),
    Prijs: round3(day.market_price_eur_per_kwh[i]),
  }));

  return (
    <div>
      <div className="flex justify-between items-baseline flex-wrap gap-2 mb-2">
        <div className="serif text-lg font-medium">Day in the life — {day.label}</div>
        <select
          className="input input-text w-auto min-w-[200px]"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          {days.map((k) => (
            <option key={k} value={k}>
              {DAY_LABELS[k] ?? k}
            </option>
          ))}
        </select>
      </div>
      <div className="help mb-3">
        kW (energy flows) en kWh (accu) op linker as, €/kWh marktprijs op rechter as.
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={data} margin={{ left: 0, right: 30, top: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD3" />
          <XAxis dataKey="uur" tick={{ fill: "#6B6661", fontSize: 11 }} interval={2} />
          <YAxis yAxisId="left" tick={{ fill: "#6B6661", fontSize: 11 }} label={{ value: "kW / kWh", angle: -90, position: "insideLeft", fill: "#6B6661", fontSize: 11 }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: "#6B6661", fontSize: 11 }} tickFormatter={(v) => `€${v.toFixed(2)}`} />
          <Tooltip
            contentStyle={{ background: "#FFFFFF", border: "1px solid #C9C0B0", borderRadius: 8, fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <ReferenceLine yAxisId="right" y={0} stroke="#8B3A1F" strokeDasharray="3 3" />
          <Line yAxisId="left" type="monotone" dataKey="Solar" stroke="#B8860B" strokeWidth={2} dot={false} />
          <Line yAxisId="left" type="monotone" dataKey="Verbruik" stroke="#1A1A1A" strokeWidth={2} dot={false} />
          <Line yAxisId="left" type="monotone" dataKey="Accu" stroke="#4A7560" strokeWidth={2} dot={false} strokeDasharray="3 3" />
          <Line yAxisId="left" type="monotone" dataKey="Import" stroke="#C66A3D" strokeWidth={1.5} dot={false} />
          <Line yAxisId="left" type="monotone" dataKey="Export" stroke="#2C4A3E" strokeWidth={1.5} dot={false} />
          <Line yAxisId="right" type="monotone" dataKey="Prijs" stroke="#8B3A1F" strokeWidth={1.5} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
